import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import Payment from 'payment';

function ReactCreditCards(props) {
    const {
        acceptedCards,
        callback,
        number,
        cvc,
        expiry,
        focused,
        issuer,
        locale,
        name,
        placeholders,
        preview
    } = props;

    /**
     * 1) Sync acceptedCards with Payment’s card array on mount & whenever
     *    acceptedCards changes (replicates what setCards() + componentDidUpdate did).
     */
    useEffect(() => {
        const setCards = () => {
            let newCardArray = [];
            if (acceptedCards.length) {
                Payment.getCardArray().forEach((d) => {
                    if (acceptedCards.includes(d.type)) {
                        newCardArray.push(d);
                    }
                });
            } else {
                // Use all cards if none explicitly accepted
                newCardArray = [...Payment.getCardArray()];
            }
            Payment.setCardArray(newCardArray);
        };
        setCards();
    }, [acceptedCards]);

    /**
     * 2) If `number` changes and a `callback` is provided, call the callback with
     *    the validation result (mirroring the old componentDidUpdate logic).
     */
    useEffect(() => {
        if (typeof callback === 'function') {
            callback(getOptions(number), Payment.fns.validateCardNumber(number));
        }
    }, [number, callback]);

    /**
     * Utility that returns the card issuer + max length for the given number
     * (same logic as the `options` getter).
     */
    function getOptions(num) {
        const cardIssuer = Payment.fns.cardType(num) || 'unknown';
        let maxLength = 16;

        if (cardIssuer === 'amex') {
            maxLength = 15;
        } else if (cardIssuer === 'dinersclub') {
            maxLength = 14;
        } else if (['hipercard', 'mastercard', 'visa'].includes(cardIssuer)) {
            maxLength = 19;
        }

        return {issuer: cardIssuer, maxLength};
    }

    // 3) The "effective" issuer in preview mode
    const {issuer: detectedIssuer, maxLength} = getOptions(number);
    const finalIssuer = preview && issuer ? issuer.toLowerCase() : detectedIssuer;

    /**
     * 4) Format the credit card number just like in the original getter.
     */
    function formatNumber(num) {
        let nextNumber =
            typeof num === 'number' ? num.toString() : num.replace(/[A-Za-z]| /g, '');

        // Only keep numeric content for non-preview mode
        if (isNaN(parseInt(nextNumber, 10)) && !preview) {
            nextNumber = '';
        }

        let effectiveMax = preview ? 19 : maxLength;
        // If the card can be 19 digits, only push to 16 if the current length <= 16
        if (effectiveMax > 16) {
            effectiveMax = nextNumber.length <= 16 ? 16 : effectiveMax;
        }
        if (nextNumber.length > effectiveMax) {
            nextNumber = nextNumber.slice(0, effectiveMax);
        }

        // Pad with • up to the max length
        while (nextNumber.length < effectiveMax) {
            nextNumber += '•';
        }

        // Format spacing by card type or length
        if (['amex', 'dinersclub'].includes(finalIssuer)) {
            const format = [0, 4, 10];
            const limit = [4, 6, 5];
            nextNumber = `${nextNumber.substr(format[0], limit[0])} ${
                nextNumber.substr(format[1], limit[1])
            } ${nextNumber.substr(format[2], limit[2])}`;
        } else if (nextNumber.length > 16) {
            const format = [0, 4, 8, 12];
            const limit = [4, 7];
            nextNumber = `${nextNumber.substr(format[0], limit[0])} ${
                nextNumber.substr(format[1], limit[0])
            } ${nextNumber.substr(format[2], limit[0])} ${
                nextNumber.substr(format[3], limit[1])
            }`;
        } else {
            // Insert a space after every group of 4
            for (let i = 1; i < effectiveMax / 4; i++) {
                const spaceIndex = i * 4 + (i - 1);
                nextNumber = `${nextNumber.slice(0, spaceIndex)} ${nextNumber.slice(
                    spaceIndex
                )}`;
            }
        }
        return nextNumber;
    }

    const formattedNumber = formatNumber(number);

    /**
     * 5) Format the expiry string exactly like in the old `expiry` getter.
     */
    function formatExpiry(exp) {
        const date = typeof exp === 'number' ? exp.toString() : exp;
        let month = '';
        let year = '';

        if (date.includes('/')) {
            [month, year] = date.split('/');
        } else if (date.length) {
            month = date.substr(0, 2);
            year = date.substr(2, 6);
        }

        while (month.length < 2) {
            month += '•';
        }
        if (year.length > 2) {
            year = year.substr(2, 4);
        }
        while (year.length < 2) {
            year += '•';
        }

        return `${month}/${year}`;
    }

    const formattedExpiry = formatExpiry(expiry);

    /**
     * 6) Render the same UI structure.
     */
    return (
        <div key="Cards" className="rccs">
            <div
                className={[
                    'rccs__card',
                    `rccs__card--${finalIssuer}`,
                    focused === 'cvc' && finalIssuer !== 'amex' ? 'rccs__card--flipped' : ''
                ]
                    .join(' ')
                    .trim()}
            >
                <div className="rccs__card--front">
                    <div className="rccs__card__background"/>
                    <div className="rccs__issuer"/>

                    <div
                        className={[
                            'rccs__cvc__front',
                            focused === 'cvc' ? 'rccs--focused' : ''
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {cvc}
                    </div>

                    <div
                        className={[
                            'rccs__number',
                            formattedNumber.replace(/ /g, '').length > 16
                                ? 'rccs__number--large'
                                : '',
                            focused === 'number' ? 'rccs--focused' : '',
                            formattedNumber.substr(0, 1) !== '•' ? 'rccs--filled' : ''
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {formattedNumber}
                    </div>

                    <div
                        className={[
                            'rccs__name',
                            focused === 'name' ? 'rccs--focused' : '',
                            name ? 'rccs--filled' : ''
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {name || placeholders?.name}
                    </div>

                    <div
                        className={[
                            'rccs__expiry',
                            focused === 'expiry' ? 'rccs--focused' : '',
                            formattedExpiry.substr(0, 1) !== '•' ? 'rccs--filled' : ''
                        ]
                            .join(' ')
                            .trim()}
                    >
                        <div className="rccs__expiry__valid">{locale?.valid}</div>
                        <div className="rccs__expiry__value">{formattedExpiry}</div>
                    </div>

                    <div className="rccs__chip"/>
                </div>

                <div className="rccs__card--back">
                    <div className="rccs__card__background"/>
                    <div className="rccs__stripe"/>
                    <div className="rccs__signature"/>
                    <div
                        className={[
                            'rccs__cvc',
                            focused === 'cvc' ? 'rccs--focused' : ''
                        ]
                            .join(' ')
                            .trim()}
                    >
                        {cvc}
                    </div>
                    <div className="rccs__issuer"/>
                </div>
            </div>
        </div>
    );
}

/* PropTypes and defaultProps remain the same */
ReactCreditCards.propTypes = {
    acceptedCards: PropTypes.array,
    callback: PropTypes.func,
    cvc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    expiry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    focused: PropTypes.string,
    issuer: PropTypes.string,
    locale: PropTypes.shape({
        valid: PropTypes.string
    }),
    name: PropTypes.string.isRequired,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    placeholders: PropTypes.shape({
        name: PropTypes.string
    }),
    preview: PropTypes.bool
};

ReactCreditCards.defaultProps = {
    acceptedCards: [],
    locale: {
        valid: 'valid thru'
    },
    placeholders: {
        name: 'YOUR NAME HERE'
    },
    preview: false
};

export default ReactCreditCards;